import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaExclamationTriangle, FaTimes, FaCreditCard, FaEnvelope, FaCheckCircle } from 'react-icons/fa';
import { useAccountStatus } from '../hooks/useAccountStatus';
import axios from 'axios';

const GlobalAccountAlert = () => {
  const { t } = useTranslation();
  const { alertMessage, hasIssues, refresh } = useAccountStatus();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleResendEmail = async () => {
    setResending(true);
    try {
      await axios.post('/resend-verification-email');
      showNotification(t('emailVerification.success'), 'success');
    } catch (error) {
      showNotification(t('emailVerification.error'), 'error');
    } finally {
      setResending(false);
    }
  };

  // Ne pas afficher l'alerte si pas de problème, si elle a été fermée, ou pour les comptes annulés
  if (!hasIssues || dismissed || !alertMessage || alertMessage.type === 'info') {
    return null;
  }

  const getAlertClasses = () => {
    switch (alertMessage.type) {
      case 'error': 
        return 'bg-red-600 border-red-700';
      case 'warning': 
        return 'bg-yellow-600 border-yellow-700';
      default: 
        return 'bg-blue-600 border-blue-700';
    }
  };

  const getActionButton = () => {
    switch (alertMessage.action) {
      case 'verify_email':
        return (
          <button
            onClick={handleResendEmail}
            disabled={resending}
            className="inline-flex items-center space-x-1 px-3 py-1 bg-white bg-opacity-20 text-white rounded-md hover:bg-opacity-30 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaEnvelope className="w-3 h-3" />
            <span>{resending ? t('emailVerification.resending') : t('emailVerification.resendEmail')}</span>
          </button>
        );
      
      case 'reactivate':
        return (
          <Link 
            to="/subscription" 
            className="inline-flex items-center space-x-1 px-3 py-1 bg-white bg-opacity-20 text-white rounded-md hover:bg-opacity-30 transition-colors text-sm font-medium"
          >
            <FaCreditCard className="w-3 h-3" />
            <span>{t('accountStatus.alerts.actions.reactivate')}</span>
          </Link>
        );
      
      case 'update_payment':
        return (
          <Link 
            to="/subscription" 
            className="inline-flex items-center space-x-1 px-3 py-1 bg-white bg-opacity-20 text-white rounded-md hover:bg-opacity-30 transition-colors text-sm font-medium"
          >
            <FaCreditCard className="w-3 h-3" />
            <span>{t('accountStatus.alerts.actions.update_payment')}</span>
          </Link>
        );
      
      default:
        return (
          <Link 
            to="/subscription" 
            className="inline-flex items-center space-x-1 px-3 py-1 bg-white bg-opacity-20 text-white rounded-md hover:bg-opacity-30 transition-colors text-sm font-medium"
          >
            <span>{t('accountStatus.alerts.actions.view_details')}</span>
          </Link>
        );
    }
  };

  return (
    <>
      {/* Notification Toast */}
      {notification && (
        <div className="fixed top-20 right-4 z-50 animate-slide-in-right">
          <div className={`rounded-lg shadow-lg p-4 min-w-[300px] max-w-md ${
            notification.type === 'success' 
              ? 'bg-green-600 text-white' 
              : 'bg-red-600 text-white'
          }`}>
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {notification.type === 'success' ? (
                  <FaCheckCircle className="w-5 h-5" />
                ) : (
                  <FaExclamationTriangle className="w-5 h-5" />
                )}
              </div>
              <p className="flex-1 text-sm font-medium">{notification.message}</p>
              <button
                onClick={() => setNotification(null)}
                className="flex-shrink-0 text-white hover:text-gray-200 transition-colors"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Banner */}
      <div className={`${getAlertClasses()} border-b text-white`}>
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center space-x-3">
              <FaExclamationTriangle className="w-4 h-4 flex-shrink-0" />
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2">
                <span className="font-medium text-sm">{alertMessage.title}</span>
                <span className="text-sm opacity-90">{alertMessage.message}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {getActionButton()}
              <button
                onClick={() => setDismissed(true)}
                className="text-white hover:text-gray-200 transition-colors"
                aria-label={t('accountStatus.alerts.close_label')}
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GlobalAccountAlert;